/**
 * Domínio: janela de datas de filtro — GUPM-264
 * ---------------------------------------------
 * Converte o que o usuário escolhe num `<input type="date">` (um dia de
 * calendário, sem hora) nos limites ISO que a query usa numa coluna
 * `timestamptz`.
 *
 * Mora aqui, e não dentro de `domain/auditoria`, porque não é regra de
 * auditoria: é tradução de fuso, usada hoje pela Trilha de Auditoria e pelo
 * Histórico de Trocas de Plantão. Tela de plantão importando do domínio de
 * auditoria seria acoplamento sem razão (ver `.claude/rules/architecture.md`).
 *
 * TS puro: sem React, sem Supabase, sem DOM.
 */

/**
 * Meia-noite LOCAL de um dia `YYYY-MM-DD`, ou `null` se a string não for um dia
 * de calendário válido (campo limpo, dia inexistente como `2026-02-31`).
 */
function meiaNoiteLocal(dia: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia);
  if (!m) return null;
  const ano = Number(m[1]);
  const mes = Number(m[2]);
  const d = Number(m[3]);
  const data = new Date(ano, mes - 1, d, 0, 0, 0, 0);
  // `new Date(2026, 1, 31)` não estoura: vira 03/03. Rejeitar pelo round-trip.
  if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== d) {
    return null;
  }
  return data;
}

/**
 * ISO do instante, ou `undefined` se sair do formato de ano com 4 dígitos.
 *
 * Acima do ano 9999 o `toISOString` emite ano expandido (`+010000-01-01T…`), que
 * o Postgres recusa com `time zone displacement out of range` — a tela cairia
 * com banner de erro. Acontece a partir de `9999-12-31` em fuso a oeste de
 * Greenwich, porque o `setDate(+1)` cruza para o ano 10000; em UTC não aparece,
 * então a checagem tem de ser sobre o texto produzido e não sobre o ano de
 * entrada. O `<input type="date">` chega a 9999 no spinner sem esforço.
 */
function isoQuePostgresAceita(data: Date | null): string | undefined {
  if (!data) return undefined;
  const iso = data.toISOString();
  return /^\d{4}-/.test(iso) ? iso : undefined;
}

/**
 * Converte o intervalo do filtro (dois dias de calendário LOCAIS) nos limites
 * ISO da query — `from` inclusivo, `to` inclusivo.
 *
 * Por que não `${dia}T00:00:00.000Z`: o sufixo `Z` consulta o dia-calendário
 * UTC, e o filtro promete o dia do fuso de quem lê. Em Brasília (UTC−3) isso
 * desloca a janela 3 h para trás — "até hoje" termina às 20:59, e as três
 * últimas horas do dia ficam fora até alguém adiantar o campo em um dia. Em
 * telas que existem para servir de prova (ANM Res. 95/2022), rótulo e janela
 * consultada discordarem é defeito, não estética. No Histórico de Trocas o
 * efeito é pior: o filtro roda sobre `completed_at`, e o turno da noite é
 * exatamente o que termina depois das 21:00 locais.
 *
 * Dia inválido não vira `Invalid Date` (que derrubaria a tela no `toISOString`):
 * devolve o limite `undefined`, e o filtro simplesmente não o aplica.
 */
export function janelaLocalDeDias(de: string, ate: string): { from?: string; to?: string } {
  const inicio = meiaNoiteLocal(de);
  const fim = meiaNoiteLocal(ate);
  if (fim) {
    // Próxima meia-noite local menos 1 ms. `setDate(+1)` respeita horário de
    // verão; somar 24 h em milissegundos erraria o dia em fuso que o adota.
    fim.setDate(fim.getDate() + 1);
  }
  return {
    from: isoQuePostgresAceita(inicio),
    to: fim ? isoQuePostgresAceita(new Date(fim.getTime() - 1)) : undefined,
  };
}

/**
 * Dia `YYYY-MM-DD` a partir dos componentes LOCAIS de uma data.
 *
 * `toISOString().slice(0, 10)` parece equivalente e não é: converte para UTC
 * antes de cortar, então depois das 21:00 em Brasília devolve o dia seguinte.
 */
export function diaLocalIso(data: Date): string {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

/** Dia de calendário LOCAL de N dias atrás — o formato do `<input type="date">`. */
export function diaLocalMenosDias(dias: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - dias);
  return diaLocalIso(d);
}
